import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, ArrowRight, User } from 'lucide-react';
import DOMPurify from 'dompurify';
import { safeJsonLd } from '../lib/safeJsonLd';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { blogPosts, BLOG_EDITORIAL_DISCLAIMER } from '../data/blogPosts';

// Article Schema component
const ArticleSchema = ({ post }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.metaDescription,
    "image": "https://socialdisputeco.com/og-image.png",
    "author": {
      "@type": "Person",
      "name": "Mark Becker",
      "jobTitle": "Founder, Social Dispute Co.",
      "worksFor": {
        "@type": "Organization",
        "name": "Social Dispute Co.",
        "url": "https://socialdisputeco.com"
      },
      "url": "https://socialdisputeco.com/about"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Social Dispute Co.",
      "logo": {
        "@type": "ImageObject",
        "url": "https://socialdisputeco.com/og-image.png"
      }
    },
    "datePublished": post.date,
    "dateModified": post.dateModified || post.date,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://socialdisputeco.com/blog/${post.slug}/`
    },
    "articleSection": post.category,
    "wordCount": post.content.split(/\s+/).length,
    "isAccessibleForFree": true
  };

  return (
    <script
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger -- JSON-LD is the W3C-recommended pattern; content is escaped via safeJsonLd
      dangerouslySetInnerHTML={{ __html: safeJsonLd(schema) }}
    />
  );
};

// HowTo Schema — included on posts whose title clearly describes a procedure
const HowToSchema = ({ post }) => {
  const titleLower = (post.title || "").toLowerCase();
  const isHowTo = /^how to|step-by-step|step by step|appeal guide|how do i/.test(titleLower);
  if (!isHowTo) return null;

  // Extract H3 (### ) lines from markdown content as steps
  const steps = post.content
    .split("\n")
    .filter((l) => l.startsWith("### "))
    .map((l) => l.replace(/^###\s+/, "").trim())
    .filter((s) => /step\s*\d/i.test(s) || /^\d+\./.test(s) || s.length < 80)
    .slice(0, 8)
    .map((name, i) => ({
      "@type": "HowToStep",
      "position": i + 1,
      "name": name,
    }));

  if (steps.length < 2) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": post.title,
    "description": post.metaDescription,
    "totalTime": "P3D",
    "step": steps,
  };

  return (
    <script
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger -- JSON-LD is the W3C-recommended pattern; content is escaped via safeJsonLd
      dangerouslySetInnerHTML={{ __html: safeJsonLd(schema) }}
    />
  );
};

// Speakable Schema for voice search
const SpeakableSchema = ({ post }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": post.title,
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": [".prose h2", ".prose p"]
    },
    "url": `https://socialdisputeco.com/blog/${post.slug}/`
  };

  return (
    <script
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger -- JSON-LD is the W3C-recommended pattern; content is escaped via safeJsonLd
      dangerouslySetInnerHTML={{ __html: safeJsonLd(schema) }}
    />
  );
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);
  
  // Update document head for SEO
  useEffect(() => {
    if (post) {
      document.title = post.metaTitle;
      
      // Update meta description
      let metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', post.metaDescription);
      }
      
      // Update or create meta keywords
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (post.keywords && post.keywords.length > 0) {
        if (!metaKeywords) {
          metaKeywords = document.createElement('meta');
          metaKeywords.setAttribute('name', 'keywords');
          document.head.appendChild(metaKeywords);
        }
        metaKeywords.setAttribute('content', post.keywords.join(', '));
      }
      
      // Update canonical URL
      let canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute('href', `https://socialdisputeco.com/blog/${post.slug}`);
      }
    }
    
    return () => {
      // Reset on unmount
      document.title = 'Social Media Account Appeal Documentation | Social Dispute Co.';
    };
  }, [post]);
  
  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  // Get related posts (same category, excluding current)
  const relatedPosts = blogPosts
    .filter(p => p.category === post.category && p.id !== post.id)
    .slice(0, 2);

  // Simple markdown-like rendering for content
  const renderContent = (content) => {
    return content.split('\n').map((line, index) => {
      // Generate unique key from content hash
      const uniqueKey = `line-${index}-${line.slice(0, 20).replace(/\s/g, '')}`;
      
      // Headers
      if (line.startsWith('## ')) {
        return <h2 key={uniqueKey} className="text-2xl font-bold text-[#d9fb06] mt-10 mb-4">{line.replace('## ', '')}</h2>;
      }
      if (line.startsWith('### ')) {
        return <h3 key={uniqueKey} className="text-xl font-bold text-[#dfddd6] mt-8 mb-3">{line.replace('### ', '')}</h3>;
      }
      // List items
      if (line.startsWith('- **')) {
        const match = line.match(/- \*\*(.+?)\*\* - (.+)/);
        if (match) {
          return (
            <li key={uniqueKey} className="text-[#888680] mb-2 ml-4">
              <strong className="text-[#dfddd6]">{match[1]}</strong> - {match[2]}
            </li>
          );
        }
      }
      if (line.startsWith('- ')) {
        return <li key={uniqueKey} className="text-[#888680] mb-2 ml-4">{line.replace('- ', '')}</li>;
      }
      // Numbered lists
      if (/^\d+\. /.test(line)) {
        return <li key={uniqueKey} className="text-[#888680] mb-2 ml-4 list-decimal">{line.replace(/^\d+\. /, '')}</li>;
      }
      // Bold text paragraphs
      if (line.startsWith('**') && line.endsWith('**')) {
        return <p key={uniqueKey} className="text-[#dfddd6] font-semibold my-4">{line.replace(/\*\*/g, '')}</p>;
      }
      // Horizontal rule
      if (line.startsWith('---')) {
        return <hr key={uniqueKey} className="border-[#3f4816] my-8" />;
      }
      // Table handling (simplified)
      if (line.startsWith('|')) {
        return null; // Skip table formatting for now
      }
      // Regular paragraphs
      if (line.trim()) {
        // Handle inline bold and links - sanitize HTML to prevent XSS
        let processedLine = line
          .replace(/\*\*(.+?)\*\*/g, '<strong class="text-[#dfddd6]">$1</strong>')
          .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-[#d9fb06] hover:underline">$1</a>');
        return <p key={uniqueKey} className="text-[#888680] leading-relaxed my-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(processedLine) }} />;
      }
      return null;
    });
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      {/* SEO Schema */}
      <ArticleSchema post={post} />
      <HowToSchema post={post} />
      <SpeakableSchema post={post} />
      
      <Header />
      
      {/* Article Header */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-[#1a1c1b] to-[#302f2c]">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-[#888680] hover:text-[#d9fb06] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
          
          <span className="inline-block px-4 py-1 bg-[#3f4816]/30 text-[#d9fb06] text-sm font-medium uppercase rounded-full mb-4">
            {post.category}
          </span>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#dfddd6] leading-tight mb-6">
            {post.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-6 text-sm text-[#888680]">
            <span className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {post.author}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
            {post.dateModified && post.dateModified !== post.date && (
              <span className="flex items-center gap-2 text-[#d9fb06]/80">
                <Calendar className="w-4 h-4" />
                Updated {new Date(post.dateModified).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
            )}
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {post.readTime}
            </span>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12 bg-[#302f2c]">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          {/* Editorial Disclaimer — Google Ads compliance */}
          <div
            className="mb-6 rounded-xl border border-[#d9fb06]/30 bg-[#1a1c1b] p-4 text-xs text-[#888680] leading-relaxed"
            data-testid="blog-editorial-disclaimer"
          >
            <strong className="text-[#d9fb06]">Editorial note:</strong> {BLOG_EDITORIAL_DISCLAIMER}
          </div>
          <div className="bg-[#1a1c1b] rounded-2xl p-8 md:p-12 border border-[#3f4816]/30">
            <div className="prose prose-invert max-w-none">
              {renderContent(post.content)}
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-[#1a1c1b]">
          <div className="max-w-4xl mx-auto px-5 md:px-10">
            <h2 className="text-2xl font-bold text-[#d9fb06] uppercase mb-8">Related Articles</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  className="group bg-[#302f2c] rounded-2xl p-6 border border-[#3f4816]/30 hover:border-[#d9fb06]/50 transition-all"
                >
                  <h3 className="text-lg font-bold text-[#dfddd6] group-hover:text-[#d9fb06] transition-colors mb-2 line-clamp-2">
                    {relatedPost.title}
                  </h3>
                  <p className="text-[#888680] text-sm line-clamp-2 mb-4">
                    {relatedPost.excerpt}
                  </p>
                  <span className="text-[#d9fb06] text-sm font-medium flex items-center gap-1">
                    Read More <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-[#302f2c]">
        <div className="max-w-3xl mx-auto px-5 md:px-10 text-center">
          <h2 className="text-2xl md:text-3xl font-black text-[#d9fb06] uppercase mb-4">
            Need Help With Your Appeal?
          </h2>
          <p className="text-[#888680] mb-8">
            Get help organizing your documentation for a stronger appeal submission.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/#intake"
              className="inline-flex items-center justify-center gap-2 bg-[#d9fb06] text-[#1a1c1b] px-8 py-4 rounded-full font-bold uppercase hover:bg-[#d9fb06]/90 transition-colors"
            >
              Start Your Case
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 border-2 border-[#d9fb06] text-[#d9fb06] px-8 py-4 rounded-full font-bold uppercase hover:bg-[#d9fb06] hover:text-[#1a1c1b] transition-colors"
            >
              Free Assessment
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
